import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Select,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { InfoOutlined as InfoOutlinedIcon, VisibilityOff, Visibility } from "@mui/icons-material";
import PageHeader from "../components/PageHeader";
import {
  useNetworkData,
  DIM_COMBINED,
  getAnswerVals,
} from "../hooks/useNetworkData";
import {
  MendelowConfig,
  MendelowEdge,
  MendelowResult,
  MendelowWeights,
  scoreMendelow,
} from "../utils/mendelow";

type RibbonTab = "view" | "scoring" | "data";

const CONTROL_LABEL_SX = { fontSize: "0.72rem" };

const DEFAULT_WEIGHTS: MendelowWeights = {
  power: { inDegree: 0.4, avgWorkWellIn: 0.6 },
  interest: { outDegree: 0.3, avgWorkWellOut: 0.5, categoryBreadthOut: 0.2 },
};

const QUADRANT_ORDER = [
  "Keep satisfied",
  "Key players",
  "Minimal effort",
  "Keep informed",
] as const;

const QUADRANT_TITLES: Record<MendelowResult["quadrant"], string> = {
  "Minimal effort": "Inform",
  "Keep informed": "Coordinate",
  "Keep satisfied": "Consult",
  "Key players": "Collaborate / Partner",
};

const QUADRANT_HELP: Record<MendelowResult["quadrant"], string> = {
  "Minimal effort": "Low power, low engagement",
  "Keep informed": "Low power, high engagement",
  "Keep satisfied": "High power, low engagement",
  "Key players": "High power, high engagement",
};

const defaultConfig = (mode: "focal" | "system"): MendelowConfig => ({
  mode,
  focalOrgId: null,
  workWellField: undefined,
  categoriesField: undefined,
  weights: DEFAULT_WEIGHTS,
  threshold: { method: "quantile", quantile: 0.65 },
});

const valueList = (items: string[]) => Array.from(new Set(items.filter(Boolean)));

const parseScore = (vals: string[] | undefined, map?: Map<string, number>) => {
  if (!vals || !vals.length) return null;
  const nums = vals
    .map((v) => {
      const direct = Number(v);
      if (Number.isFinite(direct)) return direct;
      const mapped = map?.get(v);
      if (mapped !== undefined) return mapped;
      return NaN;
    })
    .filter((n) => Number.isFinite(n));
  if (!nums.length) return null;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
};

const detectLikertField = (rawEdges: any[], fieldNames: string[]) => {
  for (const field of fieldNames) {
    const values: string[] = [];
    rawEdges.forEach((e) => {
      const vals = e.fieldVals?.[field] ?? [];
      vals.forEach((v: string) => values.push(v));
    });
    const uniq = valueList(values);
    if (!uniq.length) continue;
    const numeric = uniq.filter((v) => /^[0-9]+(\.[0-9]+)?$/.test(v));
    const within = numeric.filter((v) => Number(v) >= 1 && Number(v) <= 5);
    if (within.length >= Math.max(3, Math.floor(uniq.length * 0.6))) {
      return field;
    }
  }
  return fieldNames[0];
};

const detectCategoryField = (rawEdges: any[], fieldNames: string[], likertField?: string) => {
  const candidates = fieldNames.filter((f) => f !== likertField);
  if (!candidates.length) return fieldNames[0];
  let best: string | undefined;
  let bestCount = -1;
  candidates.forEach((field) => {
    const values: string[] = [];
    rawEdges.forEach((e) => {
      const vals = e.fieldVals?.[field] ?? [];
      vals.forEach((v: string) => values.push(v));
    });
    const uniq = valueList(values);
    if (uniq.length > bestCount) {
      bestCount = uniq.length;
      best = field;
    }
  });
  return best ?? candidates[0];
};

export default function MendelowMatrix() {
  const { id: surveyId, source, target } = useParams<{ id: string; source: string; target: string }>();
  const { rawNomEdges, nodes, dimensions, dimensionLabels, howChoiceOrder, responses, loading, error } = useNetworkData({
    surveyId,
    source,
    target,
  });

  const [ribbonTab, setRibbonTab] = useState<RibbonTab>("view");
  const [controlsOpen, setControlsOpen] = useState(true);
  const [mode, setMode] = useState<MendelowConfig["mode"]>("focal");
  const [showEdges, setShowEdges] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const [thresholdMethod, setThresholdMethod] = useState<MendelowConfig["threshold"]["method"]>("median");
  const [thresholdQuantile, setThresholdQuantile] = useState(0.5);
  const [weights, setWeights] = useState<MendelowWeights>(DEFAULT_WEIGHTS);
  const [workWellField, setWorkWellField] = useState<string | undefined>(undefined);
  const [categoriesField, setCategoriesField] = useState<string | undefined>(undefined);
  const [focalOrgId, setFocalOrgId] = useState<string | null>(null);

  const orgIds = useMemo(() => nodes.map((n) => n.id), [nodes]);
  const fieldOptions = useMemo(
    () =>
      dimensions
        .map((d) => ({
          name: d.name,
          label: dimensionLabels[d.name] || d.label || d.name,
        }))
        .filter((d) => d.name !== DIM_COMBINED),
    [dimensions, dimensionLabels]
  );
  const fieldNames = useMemo(() => fieldOptions.map((d) => d.name), [fieldOptions]);

  const workWellScale = useMemo(() => {
    if (!workWellField) return undefined;
    const order = howChoiceOrder?.[workWellField];
    if (!order || !order.length) return undefined;
    const map = new Map<string, number>();
    order.forEach((label, idx) => {
      map.set(label, idx + 1);
    });
    return map;
  }, [howChoiceOrder, workWellField]);

  const respondentOrgs = useMemo(() => {
    if (!responses.length || !source) return [];
    const names: string[] = [];
    responses.forEach((r) => {
      const vals = getAnswerVals(r.answers || {}, source);
      vals.forEach((v) => names.push(v));
    });
    return valueList(names);
  }, [responses, source]);

  useEffect(() => {
    if (!fieldOptions.length) return;
    if (!workWellField) {
      const engagementField =
        fieldOptions.find((f) => /highest level of engagement|q5\.3/i.test(f.label))?.name ??
        detectLikertField(rawNomEdges, fieldNames);
      setWorkWellField(engagementField);
    }
  }, [fieldOptions, fieldNames, rawNomEdges, workWellField]);

  useEffect(() => {
    if (!fieldOptions.length) return;
    if (!categoriesField) {
      const purposeField =
        fieldOptions.find((f) => /purposes of this relationship|q5\.2/i.test(f.label))?.name ??
        detectCategoryField(rawNomEdges, fieldNames, workWellField);
      setCategoriesField(purposeField);
    }
  }, [fieldOptions, fieldNames, rawNomEdges, workWellField, categoriesField]);

  useEffect(() => {
    if (!focalOrgId && respondentOrgs.length) {
      setFocalOrgId(respondentOrgs[0]);
    }
  }, [respondentOrgs, focalOrgId]);

  const edges = useMemo<MendelowEdge[]>(() => {
    return rawNomEdges.map((edge) => {
      const workWell = workWellField ? parseScore(edge.fieldVals?.[workWellField], workWellScale) : null;
      const categories = categoriesField ? edge.fieldVals?.[categoriesField] ?? [] : [];
      return { a: edge.a, b: edge.b, workWell, categories };
    });
  }, [rawNomEdges, workWellField, categoriesField, workWellScale]);

  const config = useMemo<MendelowConfig>(
    () => ({
      ...defaultConfig(mode),
      mode,
      focalOrgId,
      workWellField,
      categoriesField,
      weights,
      threshold: { method: thresholdMethod, quantile: thresholdQuantile },
    }),
    [mode, focalOrgId, workWellField, categoriesField, weights, thresholdMethod, thresholdQuantile]
  );

  const results = useMemo<MendelowResult[]>(() => {
    if (!orgIds.length) return [];
    return scoreMendelow(orgIds, edges, config);
  }, [orgIds, edges, config]);

  const grouped = useMemo(() => {
    const map: Record<MendelowResult["quadrant"], MendelowResult[]> = {
      "Minimal effort": [],
      "Keep informed": [],
      "Keep satisfied": [],
      "Key players": [],
    };
    results.forEach((r) => map[r.quadrant].push(r));
    return map;
  }, [results]);

  const matrixRef = useRef<HTMLDivElement | null>(null);
  const chipRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());
  const [positions, setPositions] = useState<Record<string, { x: number; y: number }>>({});

  useLayoutEffect(() => {
    if (!showEdges) return;
    const container = matrixRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const next: Record<string, { x: number; y: number }> = {};
    chipRefs.current.forEach((el, id) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      next[id] = { x: r.left - rect.left + r.width / 2, y: r.top - rect.top + r.height / 2 };
    });
    setPositions(next);
  }, [showEdges, results]);

  useEffect(() => {
    if (!showEdges) return;
    const container = matrixRef.current;
    if (!container) return;
    const obs = new ResizeObserver(() => {
      const rect = container.getBoundingClientRect();
      const next: Record<string, { x: number; y: number }> = {};
      chipRefs.current.forEach((el, id) => {
        if (!el) return;
        const r = el.getBoundingClientRect();
        next[id] = { x: r.left - rect.left + r.width / 2, y: r.top - rect.top + r.height / 2 };
      });
      setPositions(next);
    });
    obs.observe(container);
    return () => obs.disconnect();
  }, [showEdges]);

  const overlayEdges = useMemo(() => {
    if (!showEdges) return [];
    if (config.mode === "focal" && config.focalOrgId) {
      return edges.filter((e) => e.a === config.focalOrgId || e.b === config.focalOrgId);
    }
    return edges;
  }, [showEdges, edges, config.mode, config.focalOrgId]);

  const scopeStats = useMemo(() => {
    if (!edges.length) return { total: 0, focal: 0 };
    if (config.mode !== "focal" || !config.focalOrgId) return { total: edges.length, focal: 0 };
    const focalCount = edges.filter((e) => e.a === config.focalOrgId || e.b === config.focalOrgId).length;
    return { total: edges.length, focal: focalCount };
  }, [edges, config.mode, config.focalOrgId]);

  const matrixQuadrants = [
    { quadrant: "Keep satisfied" as const, position: "top-left" },
    { quadrant: "Key players" as const, position: "top-right" },
    { quadrant: "Minimal effort" as const, position: "bottom-left" },
    { quadrant: "Keep informed" as const, position: "bottom-right" },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <PageHeader title="Mendelow Matrix" subtitle="Power vs Interest view (per respondent or system-wide)" />

      <Box sx={{ position: "relative", mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.6,
            alignItems: "center",
            bgcolor: "rgba(33,150,243,0.04)",
            border: "1px solid rgba(15,76,129,0.08)",
            borderRadius: 1,
            p: 0.6,
          }}
        >
          <ToggleButtonGroup
            exclusive
            value={ribbonTab}
            size="small"
            onChange={(_, val) => val && setRibbonTab(val as RibbonTab)}
            sx={{
              borderRadius: 999,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "primary.main",
              "& .MuiToggleButton-root": {
                fontSize: "0.7rem",
                px: 1.4,
                textTransform: "uppercase",
                fontWeight: 700,
                border: 0,
                "&.Mui-selected": {
                  color: "#fff",
                  bgcolor: "primary.main",
                  "&:hover": { bgcolor: "primary.dark" },
                },
                "&:not(.Mui-selected)": {
                  color: "primary.main",
                },
              },
            }}
          >
            <ToggleButton value="view">View</ToggleButton>
            <ToggleButton value="scoring">Scoring</ToggleButton>
            <ToggleButton value="data">Data</ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ flex: 1 }} />

          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              if (surveyId && source && target) {
                window.location.assign(`/network/${surveyId}/${source}/${target}`);
              }
            }}
          >
            Back to Network
          </Button>
          <Button size="small" variant="outlined" startIcon={controlsOpen ? <VisibilityOff /> : <Visibility />} onClick={() => setControlsOpen((v) => !v)}>
            {controlsOpen ? "Hide Controls" : "Show Controls"}
          </Button>
        </Box>

        {controlsOpen && (
          <Box
            sx={{
              mt: 0.6,
              display: "flex",
              flexWrap: "nowrap",
              gap: 1,
              overflowX: "auto",
              borderTop: "1px solid rgba(0,0,0,0.06)",
              pt: 0.6,
            }}
          >
            {ribbonTab === "view" && (
              <Box sx={{ minWidth: 420 }}>
                <Typography variant="overline" sx={{ ...CONTROL_LABEL_SX, color: "text.secondary", letterSpacing: "0.08em" }}>
                  VIEW
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                  <ToggleButtonGroup
                    exclusive
                    value={mode}
                    size="small"
                    onChange={(_, val) => val && setMode(val as MendelowConfig["mode"])}
                  >
                    <ToggleButton value="focal">Focal</ToggleButton>
                    <ToggleButton value="system">System</ToggleButton>
                  </ToggleButtonGroup>
                  <FormControlLabel
                    control={<Checkbox size="small" checked={showEdges} onChange={(e) => setShowEdges(e.target.checked)} />}
                    label={<Typography variant="caption" sx={CONTROL_LABEL_SX}>Show edges overlay</Typography>}
                    sx={{ m: 0 }}
                  />
                  <FormControlLabel
                    control={<Checkbox size="small" checked={showDebug} onChange={(e) => setShowDebug(e.target.checked)} />}
                    label={<Typography variant="caption" sx={CONTROL_LABEL_SX}>Show debug metrics</Typography>}
                    sx={{ m: 0 }}
                  />
                </Stack>
                {mode === "focal" && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" sx={CONTROL_LABEL_SX}>Focal organisation</Typography>
                    <Select
                      size="small"
                      value={focalOrgId ?? ""}
                      onChange={(e) => setFocalOrgId(e.target.value as string)}
                      sx={{ minWidth: 220, ml: 1 }}
                    >
                      {respondentOrgs.map((org) => (
                        <MenuItem key={org} value={org}>{org}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                )}
              </Box>
            )}

            {ribbonTab === "scoring" && (
              <Box sx={{ minWidth: 520 }}>
                <Typography variant="overline" sx={{ ...CONTROL_LABEL_SX, color: "text.secondary", letterSpacing: "0.08em" }}>
                  SCORING
                </Typography>
                <Stack spacing={1} sx={{ mt: 0.5 }}>
                  <Box>
                    <Typography variant="caption" sx={CONTROL_LABEL_SX}>Power weight: in-degree ({weights.power.inDegree.toFixed(2)})</Typography>
                    <Slider
                      size="small"
                      min={0}
                      max={1}
                      step={0.05}
                      value={weights.power.inDegree}
                      onChange={(_, v) => setWeights((w) => ({ ...w, power: { ...w.power, inDegree: v as number } }))}
                    />
                    <Typography variant="caption" sx={CONTROL_LABEL_SX}>Power weight: avg work well ({weights.power.avgWorkWellIn.toFixed(2)})</Typography>
                    <Slider
                      size="small"
                      min={0}
                      max={1}
                      step={0.05}
                      value={weights.power.avgWorkWellIn}
                      onChange={(_, v) => setWeights((w) => ({ ...w, power: { ...w.power, avgWorkWellIn: v as number } }))}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={CONTROL_LABEL_SX}>Interest weight: out-degree ({weights.interest.outDegree.toFixed(2)})</Typography>
                    <Slider
                      size="small"
                      min={0}
                      max={1}
                      step={0.05}
                      value={weights.interest.outDegree}
                      onChange={(_, v) => setWeights((w) => ({ ...w, interest: { ...w.interest, outDegree: v as number } }))}
                    />
                    <Typography variant="caption" sx={CONTROL_LABEL_SX}>Interest weight: avg work well ({weights.interest.avgWorkWellOut.toFixed(2)})</Typography>
                    <Slider
                      size="small"
                      min={0}
                      max={1}
                      step={0.05}
                      value={weights.interest.avgWorkWellOut}
                      onChange={(_, v) => setWeights((w) => ({ ...w, interest: { ...w.interest, avgWorkWellOut: v as number } }))}
                    />
                    <Typography variant="caption" sx={CONTROL_LABEL_SX}>Interest weight: category breadth ({weights.interest.categoryBreadthOut.toFixed(2)})</Typography>
                    <Slider
                      size="small"
                      min={0}
                      max={1}
                      step={0.05}
                      value={weights.interest.categoryBreadthOut}
                      onChange={(_, v) => setWeights((w) => ({ ...w, interest: { ...w.interest, categoryBreadthOut: v as number } }))}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={CONTROL_LABEL_SX}>Threshold method</Typography>
                    <Select size="small" value={thresholdMethod} onChange={(e) => setThresholdMethod(e.target.value as any)} sx={{ ml: 1 }}>
                      <MenuItem value="median">Median</MenuItem>
                      <MenuItem value="quantile">Quantile</MenuItem>
                    </Select>
                    {thresholdMethod === "quantile" && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={CONTROL_LABEL_SX}>Quantile: {thresholdQuantile.toFixed(2)}</Typography>
                        <Slider
                          size="small"
                          min={0.1}
                          max={0.9}
                          step={0.05}
                          value={thresholdQuantile}
                          onChange={(_, v) => setThresholdQuantile(v as number)}
                        />
                      </Box>
                    )}
                  </Box>
                </Stack>
              </Box>
            )}

            {ribbonTab === "data" && (
              <Box sx={{ minWidth: 420 }}>
                <Typography variant="overline" sx={{ ...CONTROL_LABEL_SX, color: "text.secondary", letterSpacing: "0.08em" }}>
                  DATA FIELDS
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", mt: 0.5 }}>
                  <Box>
                    <Typography variant="caption" sx={CONTROL_LABEL_SX}>Work-well field</Typography>
                    <Select
                      size="small"
                      value={workWellField ?? ""}
                      onChange={(e) => setWorkWellField(e.target.value as string)}
                      sx={{ minWidth: 260 }}
                      disabled={!fieldOptions.length}
                    >
                      {!fieldOptions.length && <MenuItem value="">None</MenuItem>}
                      {fieldOptions.map((f) => (
                        <MenuItem key={f.name} value={f.name}>{f.label}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={CONTROL_LABEL_SX}>Categories field</Typography>
                    <Select
                      size="small"
                      value={categoriesField ?? ""}
                      onChange={(e) => setCategoriesField(e.target.value as string)}
                      sx={{ minWidth: 260 }}
                      disabled={!fieldOptions.length}
                    >
                      {!fieldOptions.length && <MenuItem value="">None</MenuItem>}
                      {fieldOptions.map((f) => (
                        <MenuItem key={f.name} value={f.name}>{f.label}</MenuItem>
                      ))}
                    </Select>
                  </Box>
                </Stack>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {loading && <Typography>Loading network data…</Typography>}
      {error && <Typography color="error">{error}</Typography>}

      <Box
        ref={matrixRef}
        sx={{
          position: "relative",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "1fr 1fr",
          gap: 2,
          minHeight: 520,
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: 2,
          p: 2,
          bgcolor: "#fff",
        }}
      >
        {showEdges && (
          <svg
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
          >
            <defs>
              <marker id="mendelow-arrow" viewBox="0 -5 10 10" refX="10" refY="0" markerWidth="6" markerHeight="6" orient="auto">
                <path d="M0,-5L10,0L0,5" fill="rgba(55,55,55,0.35)" />
              </marker>
            </defs>
            {overlayEdges.map((edge, idx) => {
              const a = positions[edge.a];
              const b = positions[edge.b];
              if (!a || !b) return null;
              return (
                <line
                  key={`${edge.a}-${edge.b}-${idx}`}
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  stroke="rgba(55,55,55,0.25)"
                  strokeWidth={1}
                  markerEnd="url(#mendelow-arrow)"
                />
              );
            })}
          </svg>
        )}

        {matrixQuadrants.map((q) => (
          <Box
            key={q.quadrant}
            sx={{
              border: "1px dashed rgba(0,0,0,0.15)",
              borderRadius: 2,
              p: 1.5,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              bgcolor: "rgba(248,250,252,0.9)",
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {QUADRANT_TITLES[q.quadrant]}
              </Typography>
              <Tooltip title={QUADRANT_HELP[q.quadrant]}>
                <InfoOutlinedIcon fontSize="inherit" sx={{ color: "text.secondary" }} />
              </Tooltip>
            </Stack>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.6 }}>
              {(grouped[q.quadrant] || []).map((org) => (
                <Tooltip
                  key={org.orgId}
                  title={
                    <Box sx={{ p: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>{org.orgId}</Typography>
                      <Typography variant="caption">Power: {org.powerScore.toFixed(2)}</Typography>
                      <Typography variant="caption">Interest: {org.interestScore.toFixed(2)}</Typography>
                      <Typography variant="caption">In-degree: {org.inDegree}</Typography>
                      <Typography variant="caption">Out-degree: {org.outDegree}</Typography>
                      <Typography variant="caption">Avg work well (in): {org.avgWorkWellIn.toFixed(2)}</Typography>
                      <Typography variant="caption">Avg work well (out): {org.avgWorkWellOut.toFixed(2)}</Typography>
                      <Typography variant="caption">Category breadth: {org.categoryBreadthOut}</Typography>
                    </Box>
                  }
                  placement="top"
                >
                  <Box
                    ref={(el: HTMLDivElement | null) => {
                      chipRefs.current.set(org.orgId, el);
                    }}
                    onClick={() => {
                      if (mode === "focal") setFocalOrgId(org.orgId);
                    }}
                    sx={{ cursor: mode === "focal" ? "pointer" : "default" }}
                  >
                    <Chip
                      size="small"
                      label={org.orgId}
                      variant={focalOrgId === org.orgId ? "filled" : "outlined"}
                      color={focalOrgId === org.orgId ? "primary" : "default"}
                    />
                  </Box>
                </Tooltip>
              ))}
            </Box>
          </Box>
        ))}

        <Box sx={{ position: "absolute", top: 8, left: "50%", transform: "translateX(-50%)", fontSize: 12, color: "text.secondary" }}>
          Engagement →
        </Box>
        <Box sx={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%) rotate(-90deg)", fontSize: 12, color: "text.secondary" }}>
          Power →
        </Box>
      </Box>

      {showDebug && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Debug metrics (sorted by power → interest)
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
            Mode: {mode === "focal" ? `Focal (${focalOrgId ?? "none"})` : "System"} ·
            Total edges: {scopeStats.total} ·
            {mode === "focal" ? `Edges involving focal: ${scopeStats.focal}` : "Using all edges"}
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <Box component="thead">
                <Box component="tr">
                  {[
                    "Org",
                    "Quadrant",
                    "Power",
                    "Interest",
                    "In-degree",
                    "Out-degree",
                    "Avg Q5.3 In",
                    "Avg Q5.3 Out",
                    "Category breadth",
                  ].map((h) => (
                    <Box key={h} component="th" sx={{ textAlign: "left", borderBottom: "1px solid #ddd", p: 0.6 }}>
                      {h}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {[...results]
                  .sort((a, b) => b.powerScore - a.powerScore || b.interestScore - a.interestScore)
                  .map((r) => (
                    <Box key={r.orgId} component="tr">
                      <Box component="td" sx={{ borderBottom: "1px solid #eee", p: 0.6 }}>{r.orgId}</Box>
                      <Box component="td" sx={{ borderBottom: "1px solid #eee", p: 0.6 }}>{QUADRANT_TITLES[r.quadrant]}</Box>
                      <Box component="td" sx={{ borderBottom: "1px solid #eee", p: 0.6 }}>{r.powerScore.toFixed(2)}</Box>
                      <Box component="td" sx={{ borderBottom: "1px solid #eee", p: 0.6 }}>{r.interestScore.toFixed(2)}</Box>
                      <Box component="td" sx={{ borderBottom: "1px solid #eee", p: 0.6 }}>{r.inDegree}</Box>
                      <Box component="td" sx={{ borderBottom: "1px solid #eee", p: 0.6 }}>{r.outDegree}</Box>
                      <Box component="td" sx={{ borderBottom: "1px solid #eee", p: 0.6 }}>{r.avgWorkWellIn.toFixed(2)}</Box>
                      <Box component="td" sx={{ borderBottom: "1px solid #eee", p: 0.6 }}>{r.avgWorkWellOut.toFixed(2)}</Box>
                      <Box component="td" sx={{ borderBottom: "1px solid #eee", p: 0.6 }}>{r.categoryBreadthOut}</Box>
                    </Box>
                  ))}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}
